import { Dropdown } from 'antd'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { API } from '@/api'
import ArrowDownIcon from '@/assets/icons/arrow-down.svg?react'
import SearchIcon from '@/assets/icons/search.svg?react'
import { Trans } from '@/components'
import { TeamRole } from '@/consts/team'
import { useTeamStore } from '@/stores/team'

interface TeamMember {
  resID: string
  user: {
    name: string
    nickname?: string
    role: TeamRole
  }
}

interface MemberFilterProps {
  value: string[]
  onChange: (memberIds: string[]) => void
}

export function MemberFilter({ value, onChange }: MemberFilterProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [members, setMembers] = useState<TeamMember[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const teamStore = useTeamStore()

  // Helper function to get translated role label
  const getRoleLabel = (role: TeamRole): string => {
    if (role === TeamRole.Owner) return t('team.role.owner')
    if (role === TeamRole.Admin) return t('team.role.admin')
    return t('team.role.member')
  }

  useEffect(() => {
    if (open && teamStore.teamTokenData?.teamResID && members.length === 0) {
      fetchMembers()
    }
  }, [open, teamStore.teamTokenData?.teamResID, members.length])

  const fetchMembers = async () => {
    try {
      const res = await API.getTeamMemberList({
        res_id: teamStore.teamTokenData!.teamResID,
        page: 1,
        per_page: 100, // Fetch all members for filter
      })
      if (res.code === 0 && res.members) {
        setMembers(res.members)
      }
    } catch (error) {
      console.error('Failed to fetch team members:', error)
    }
  }

  const handleToggleMember = (memberId: string) => {
    if (value.includes(memberId)) {
      onChange(value.filter((id) => id !== memberId))
    } else {
      onChange([...value, memberId])
    }
  }

  const handleSelectAll = (memberIds: string[]) => {
    if (memberIds.every((id) => value.includes(id))) {
      // Deselect all
      onChange(value.filter((id) => !memberIds.includes(id)))
    } else {
      // Select all
      const newSelected = [...new Set([...value, ...memberIds])]
      onChange(newSelected)
    }
  }

  const { filteredMembers, ownerAndAdmins, normalMembers } = useMemo(() => {
    const normalizedSearch = searchTerm.toLowerCase()
    const filtered: TeamMember[] = []
    const owners: TeamMember[] = []
    const normals: TeamMember[] = []

    for (const m of members) {
      const displayName = (m.user.nickname || m.user.name).toLowerCase()
      if (!displayName.includes(normalizedSearch)) continue
      filtered.push(m)
      if (m.user.role === TeamRole.Owner || m.user.role === TeamRole.Admin) {
        owners.push(m)
      } else if (m.user.role === TeamRole.Normal) {
        normals.push(m)
      }
    }

    return { filteredMembers: filtered, ownerAndAdmins: owners, normalMembers: normals }
  }, [members, searchTerm])

  const dropdownContent = (
    <div className="bg-panel-white rounded-lg shadow-lg w-[320px]">
      {/* Search */}
      <div className="p-2 border-b border-neutral-200">
        <div className="flex items-center gap-2 px-3 py-2">
          <SearchIcon className="w-6 h-6" />
          <input
            type="text"
            placeholder={t('team.admin.filter.search', 'Search')}
            className="flex-1 t-s-sb text-primary outline-none bg-transparent"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <div className="max-h-64 overflow-y-auto">
        {/* Owner & Admin Group */}
        {ownerAndAdmins.length > 0 && (
          <>
            <div className="flex items-center justify-between px-4 py-2 bg-panel-white sticky top-0">
              <span className="t-xs text-tertiary">
                <Trans
                  i18nKey="team.admin.filter.founderAndAdmin"
                  values={{ count: ownerAndAdmins.length }}
                >
                  Founder & Admin ({ownerAndAdmins.length})
                </Trans>
              </span>
              <button
                type="button"
                className="t-xs text-highlight hover:underline"
                onClick={() => handleSelectAll(ownerAndAdmins.map((m) => m.resID))}
              >
                <Trans i18nKey="team.admin.filter.selectAll">Select All</Trans>
              </button>
            </div>
            {ownerAndAdmins.map((member) => (
              <div
                key={member.resID}
                className="flex items-center justify-between px-4 py-2 hover:bg-panel-gray-100 cursor-pointer"
                onClick={() => handleToggleMember(member.resID)}
              >
                <div className="flex items-center gap-2 flex-1">
                  <input
                    type="checkbox"
                    checked={value.includes(member.resID)}
                    onChange={() => handleToggleMember(member.resID)}
                    className="w-5 h-5 checked:bg-btn-secondary"
                  />
                  <span className="t-s-sb text-primary">
                    {member.user.nickname || member.user.name}
                  </span>
                </div>
                <span className="t-xs text-tertiary">{getRoleLabel(member.user.role)}</span>
              </div>
            ))}
          </>
        )}

        {/* Normal Members Group */}
        {normalMembers.length > 0 && (
          <>
            <div className="flex items-center justify-between px-4 py-2 bg-panel-white sticky top-0">
              <span className="t-xs text-tertiary">
                <Trans
                  i18nKey="team.admin.filter.normalMember"
                  values={{ count: normalMembers.length }}
                >
                  Member ({normalMembers.length})
                </Trans>
              </span>
              <button
                type="button"
                className="t-xs text-highlight hover:underline"
                onClick={() => handleSelectAll(normalMembers.map((m) => m.resID))}
              >
                <Trans i18nKey="team.admin.filter.selectAll">Select All</Trans>
              </button>
            </div>
            {normalMembers.map((member) => (
              <div
                key={member.resID}
                className="flex items-center justify-between px-4 py-2 hover:bg-panel-gray-100 cursor-pointer"
                onClick={() => handleToggleMember(member.resID)}
              >
                <div className="flex items-center gap-2 flex-1">
                  <input
                    type="checkbox"
                    checked={value.includes(member.resID)}
                    onChange={() => handleToggleMember(member.resID)}
                    className="w-5 h-5 checked:bg-btn-secondary"
                  />
                  <span className="t-s-sb text-primary">
                    {member.user.nickname || member.user.name}
                  </span>
                </div>
                <span className="t-xs text-tertiary">{getRoleLabel(member.user.role)}</span>
              </div>
            ))}
          </>
        )}

        {filteredMembers.length === 0 && (
          <div className="px-4 py-8 text-center t-sm text-tertiary">
            <Trans i18nKey="team.admin.filter.noMembersFound">No members found</Trans>
          </div>
        )}
      </div>
    </div>
  )

  return (
    <div className="flex flex-col gap-2">
      <label className="t-sb text-secondary">
        <Trans i18nKey="team.admin.filter.member">Member</Trans>
      </label>
      <Dropdown
        trigger={['click']}
        open={open}
        onOpenChange={setOpen}
        popupRender={() => dropdownContent}
        placement="bottomLeft"
      >
        <button
          type="button"
          className="border border-neutral-200 rounded-lg px-4 py-3 h-11 w-[180px] flex items-center justify-between hover:border-secondary transition-colors"
        >
          <div className="flex items-center gap-2">
            {value.length > 0 && (
              <div className="bg-highlight/10 rounded-full px-2 py-1 min-w-[24px] h-[24px] flex items-center justify-center">
                <span className="t-sb text-highlight">{value.length}</span>
              </div>
            )}

            {value.length === 0 && (
              <span className="t-s-sb">
                <Trans i18nKey="team.admin.filter.all">All</Trans>
              </span>
            )}
          </div>
          <ArrowDownIcon className="w-5 h-5 text-tertiary" />
        </button>
      </Dropdown>
    </div>
  )
}
